import {
  Button,
  CircularProgress,
  IconButton,
  Link,
  List,
  ListItem,
  Modal,
  Box,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Close, tpTheme } from "@trainingpeaks/designtokens";
import { DeleteAccountModalState } from "@hooks/useHandleDeleteAccount";

const baseModalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  border: "none",
  borderRadius: 2,
  boxShadow: 24,
};

interface DeleteAccountModalProps {
  open: boolean;
  modalState: DeleteAccountModalState;
  isProcessing: boolean;
  onClose: () => void;
  onXClose: () => void;
  onConfirm: () => void;
  onSuccessClose: () => void;
}

const DeleteAccountModal = ({
  open,
  modalState,
  isProcessing,
  onClose,
  onXClose,
  onConfirm,
  onSuccessClose,
}: DeleteAccountModalProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  const modalStyle = { ...baseModalStyle, width: isNarrow ? "90%" : "50%" };

  const largeScreenButtonGroup = (
    <div className="flex justify-end items-center gap-4 mt-6">
      <Button variant="text" color="inherit" onClick={onClose} disabled={isProcessing}>
        {t("Cancel")}
      </Button>
      <Button variant="contained" color="error" onClick={onConfirm} disabled={isProcessing}>
        {isProcessing ? <CircularProgress size={20} color="inherit" /> : t("Proceed")}
      </Button>
    </div>
  );

  const smallScreenButtonGroup = (
    <div className="flex flex-col justify-around items-center w-full h-24 mt-4">
      <Button
        variant="contained"
        color="error"
        onClick={onConfirm}
        disabled={isProcessing}
        className="w-full"
      >
        {isProcessing ? <CircularProgress size={20} color="inherit" /> : t("Proceed")}
      </Button>
      <Button variant="text" color="inherit" className="w-full" onClick={onClose} disabled={isProcessing}>
        {t("Cancel")}
      </Button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={modalState === "confirm" ? onClose : undefined}
      aria-labelledby="delete-account-modal-title"
      aria-describedby="delete-account-modal-description"
    >
      <Box sx={modalStyle} className="p-6" data-testid="delete-account-modal">
        <Box className="flex justify-end h-6">
          <IconButton
            onClick={onXClose}
            aria-label={t("Close")}
            className="px-0"
            data-testid="modal-x-button"
          >
            <Close size="24" fill={tpTheme.semanticLayer.colors.disabled.dark} />
          </IconButton>
        </Box>

        {modalState === "confirm" && (
          <>
            <Typography id="delete-account-modal-title" variant="h2" className="font-bold mb-4">
              {t("Verify you would like to delete your account")}
            </Typography>
            <Typography id="delete-account-modal-description" className="mb-3">
              {t("Before you go, if there is anything we can do to help, please")}{" "}
              <Link
                href="https://help.trainingpeaks.com/hc/en-us/requests/new"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("Send a Help Request")}
              </Link>{" "}
              {t("to our Customer Success team.")}
            </Typography>
            <Typography className="mb-2">
              {t("If you proceed, please be aware that the following actions will be taken on your account:")}
            </Typography>
            <List className="list-disc pl-8 py-0" disablePadding>
              <ListItem disablePadding className="list-item">
                <Typography>{t("Your subscription will be cancelled")}</Typography>
              </ListItem>
              <ListItem disablePadding className="list-item">
                <Typography>{t("You will no longer be able to login or access your account")}</Typography>
              </ListItem>
            </List>
            {isMobile || isNarrow ? smallScreenButtonGroup : largeScreenButtonGroup}
          </>
        )}

        {modalState === "success" && (
          <>
            <Typography id="delete-account-modal-title" variant="h2" className="font-bold mb-4">
              {t("We're sorry to see you go!")}
            </Typography>
            <Typography id="delete-account-modal-description">{t("Your account has been deleted.")}</Typography>
            <Box className="flex justify-end mt-6">
              <Button
                variant="contained"
                color="primary"
                onClick={onSuccessClose}
                data-testid="modal-success-close-button"
              >
                {t("Close")}
              </Button>
            </Box>
          </>
        )}

        {modalState === "failure" && (
          <>
            <Typography id="delete-account-modal-title" variant="h2" className="font-bold mb-4">
              {t("Your account was not deleted.")}
            </Typography>
            <Typography id="delete-account-modal-description">
              {t("There was a problem deleting your account. Please")}{" "}
              <Link
                href="https://help.trainingpeaks.com/anonymous_requests/new"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("contact Customer Success")}
              </Link>{" "}
              {t("and they will be able to process your request to delete your account.")}
            </Typography>
            <Box className="flex justify-end mt-6">
              <Button variant="contained" color="primary" onClick={onClose}>
                {t("Close")}
              </Button>
            </Box>
          </>
        )}

        {modalState === "coachMatchFailure" && (
          <>
            <Typography id="delete-account-modal-title" variant="h2" className="font-bold mb-4">
              {t("Your account was not deleted.")}
            </Typography>
            <Typography id="delete-account-modal-description">
              {t("As a TrainingPeaks Coach Match Athlete, please")}{" "}
              <Link href="https://help.trainingpeaks.com/tickets/new" target="_blank" rel="noopener noreferrer">
                {t("submit a request")}
              </Link>{" "}
              {t("to delete your account.")}
            </Typography>
            <Box className="flex justify-end mt-6">
              <Button variant="contained" color="primary" onClick={onClose}>
                {t("Close")}
              </Button>
            </Box>
          </>
        )}
      </Box>
    </Modal>
  );
};

export default DeleteAccountModal;
